import { EditableCell } from '@/components/insurance/EditableCell';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type { IEditableJsonTableProps } from '@/@types/insurance';

const BADGE_STYLES: Record<string, string> = {
  green: 'bg-green-50 text-green-700 light:bg-green-100',
  blue: 'bg-blue-50 text-blue-500 light:bg-blue-100',
  orange: 'bg-orange-50 text-orange-500 light:bg-orange-100',
  red: 'bg-red-50 text-red-500 light:bg-red-100',
  violet: 'bg-violet-100 text-violet-600 light:bg-violet-150',
};

export function EditableJsonTable<T extends { id: string }>({
  rows,
  columns,
  onSave,
  emptyMessage = 'No entries yet.',
}: IEditableJsonTableProps<T>) {
  function handleCellSave(rowIndex: number, key: keyof T, newValue: string) {
    const updated = rows.map((row, i) => {
      if (i !== rowIndex) return row;
      return { ...row, [key]: newValue };
    });
    onSave(updated);
  }

  return (
    <div className="rounded-xl border bg-card overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50">
            {columns.map(col => (
              <TableHead
                key={String(col.key)}
                className="text-[11px] font-semibold text-muted-foreground"
                style={col.width ? { width: col.width, minWidth: col.width } : undefined}
              >
                {col.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="text-center text-sm text-muted-foreground py-6"
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row, rowIndex) => (
              <TableRow key={row.id} className={cn(rowIndex % 2 !== 0 && 'bg-muted/20')}>
                {columns.map(col => {
                  const cellValue = String(row[col.key] ?? '');
                  const isId = col.key === 'id';
                  const badgeColor = col.badgeColorMap?.[cellValue];
                  const isDanger = col.variant === 'danger';
                  return (
                    <TableCell
                      key={String(col.key)}
                      className={cn('py-3 px-4', isId && 'text-xs text-muted-foreground font-mono')}
                    >
                      {badgeColor && cellValue ? (
                        <span
                          className={cn(
                            'inline-flex items-center rounded-[10px] px-2.5 py-0.5 text-[11px] font-medium',
                            BADGE_STYLES[badgeColor] ?? BADGE_STYLES.green
                          )}
                        >
                          {cellValue}
                        </span>
                      ) : isDanger && cellValue ? (
                        <span className="inline-flex items-center rounded-[10px] px-2.5 py-0.5 text-[11px] font-medium bg-red-50 text-red-500">
                          {cellValue}
                        </span>
                      ) : (
                        <EditableCell
                          value={cellValue}
                          readOnly={col.readOnly}
                          onSave={next => handleCellSave(rowIndex, col.key, next)}
                          className={cn(
                            isId ? 'text-xs text-muted-foreground font-mono' : 'text-xs',
                            col.readOnly && isId && 'text-muted-foreground'
                          )}
                        />
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
